import { useEffect, useMemo, useState } from "react";
import {
  Stack,
  Dialog,
  Button,
  Typography,
  IconButton,
  DialogTitle,
  DialogActions,
  DialogContent
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import { Close, WhatsApp } from "@material-ui/icons";
import { ListingFilePreviewCard } from "../../modal/core/ListingFilesUpload";
import { NewMultipleContactPicker } from "components/_dashboard/listingsMatch/NewContactPicker";
import { formatBytesToHumanReadableSize } from "utils/listings/display";
import type { TFunction } from "react-i18next";
import type { FilePreview } from "../../../../../@types/listings/fileData";
import type { RealEstateContact } from "../../../../../@types/sharedSchema";
import type { ListingWithAdditionalData } from "../../../../../@types/listings/new";

type ListingTableFilesShareDialogProps = {
  open: boolean;
  files: FilePreview[];
  listing: ListingWithAdditionalData;
  onClose: () => void;
};

export function ListingTableFilesShareDialog({
  open,
  files,
  listing,
  onClose
}: ListingTableFilesShareDialogProps): JSX.Element {
  const [selectedFiles, setSelectedFiles] = useState<FilePreview[]>(files);
  const [selectedContacts, setSelectedContacts] = useState<RealEstateContact[]>([]);

  const { t: translate } = useTranslation();

  useEffect(() => {
    setSelectedFiles(files);
    setSelectedContacts([]);
  }, [files]);

  const handleSelectFile = (id: string) => (): void => {
    const isSelected = selectedFiles.some((file) => file.id === id);

    if (isSelected) {
      const filteredFiles = selectedFiles.filter((file) => file.id !== id);
      setSelectedFiles(filteredFiles);
    } else {
      const selectedFile = files.find((file) => file.id === id);

      if (!selectedFile) return;

      setSelectedFiles([...selectedFiles, selectedFile]);
    }
  };

  const isFileSelected = (id: string): boolean => {
    return selectedFiles.some((file) => file.id === id);
  };

  const handleShareContacts = (): void => {
    for (const { id: contactPhone } of selectedContacts) {
      const parsedPhoneNumber = (contactPhone ?? "").replace(/\D/g, "");

      const parsedFiles = selectedFiles
        .map(
          ({ name, nameOverride, size, src }) =>
            `- ${nameOverride || name} (${formatBytesToHumanReadableSize(size)}) | ${src}`
        )
        .join("\n\n");

      const parsedMessage = encodeURIComponent(`Hello, check out these files: \n\n${parsedFiles}`);

      const whatsappUrl = `https://api.whatsapp.com/send?phone=${parsedPhoneNumber}&text=${parsedMessage}`;

      window.open(whatsappUrl, "_blank");
    }
  };

  // The parties to THIS listing — its sellers, buyers and their attorneys — listed above the
  // picker search results. They are named on the listing and need not be contacts at all,
  // which is why the picker cannot find them by searching and hands them back as they are.
  // (This used to be concatenated with the whole contact book, downloaded for the purpose.)
  const listingParties = useMemo(() => getListingParties(listing, translate), [listing, translate]);

  const isShareFileButtonDisabled = !selectedContacts.length || !selectedFiles.length;

  return (
    <Dialog fullWidth open={open} maxWidth="md" onClose={onClose}>
      <DialogTitle sx={{ py: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{translate("listingsPage.shareListingFiles")}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ py: 4 }}>
        <Stack gap={4}>
          <Stack gap={2}>
            <Typography variant="subtitle2">
              {translate("listingsPage.selectFilesToShareWith")}
            </Typography>
            <Stack gap={2}>
              {files?.map((filePreview) => (
                <ListingFilePreviewCard
                  file={filePreview}
                  selectProps={{
                    checked: isFileSelected(filePreview.id),
                    selectFile: handleSelectFile(filePreview.id)
                  }}
                  key={filePreview.id}
                />
              ))}
            </Stack>
          </Stack>
          <Stack gap={2}>
            <Typography variant="subtitle2">
              {translate("listingsPage.selectContactsToShareWith")}
            </Typography>
            <NewMultipleContactPicker
              pinnedOptions={listingParties}
              selectedContacts={selectedContacts}
              setSelectedContacts={setSelectedContacts}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", gap: 2 }}>
        <Button variant="contained" color="error" onClick={onClose}>
          {translate("general.cancel")}
        </Button>
        <LoadingButton
          type="submit"
          color="primary"
          variant="contained"
          disabled={isShareFileButtonDisabled}
          startIcon={<WhatsApp />}
          onClick={handleShareContacts}
        >
          {translate("listingsPage.control.shareInWhatsApp")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function getListingParties(listing: ListingWithAdditionalData, translate: TFunction): RealEstateContact[] {
  function createContact(
    phoneNumber: string | undefined | null,
    name: string | undefined | null,
    label: string
  ): RealEstateContact | null {
    if (!phoneNumber) return null;

    const parsedName = name ? `${name} (${label})` : label;

    return {
      id: phoneNumber,
      callerFirstName: parsedName
    } as RealEstateContact;
  }

  const customContactsMap = new Map<string, RealEstateContact>();

  const {
    ownersData,
    buyerOriginData,
    buyerAttorneyName,
    buyerAttorneyPhoneNumber,
    sellerAttorneyName,
    sellerAttorneyPhoneNumber
  } = listing;

  if (Array.isArray(ownersData)) {
    for (const owner of ownersData) {
      const newContact = createContact(
        owner.phoneNumber,
        owner.name,
        translate("listingsPage.seller")
      );

      if (newContact) {
        customContactsMap.set(newContact.id, newContact);
      }
    }
  }

  if (Array.isArray(buyerOriginData)) {
    for (const buyer of buyerOriginData) {
      const newContact = createContact(
        buyer.phoneNumber,
        buyer.name,
        translate("listingsPage.buyer")
      );

      if (newContact) {
        customContactsMap.set(newContact.id, newContact);
      }
    }
  }

  const buyerAttorneyContact = createContact(
    buyerAttorneyPhoneNumber,
    buyerAttorneyName,
    translate("listingsPage.buyerAttorney")
  );

  if (buyerAttorneyContact) {
    customContactsMap.set(buyerAttorneyContact.id, buyerAttorneyContact);
  }

  const sellerAttorneyContact = createContact(
    sellerAttorneyPhoneNumber,
    sellerAttorneyName,
    translate("listingsPage.sellerAttorney")
  );

  if (sellerAttorneyContact) {
    customContactsMap.set(sellerAttorneyContact.id, sellerAttorneyContact);
  }

  return Array.from(customContactsMap.values());
}
